/**
 * Pure drawing functions for the timing graph.
 * Accepts any Canvas 2D-compatible context (real OffscreenCanvas ctx or canvas2svg C2S).
 */

import { GRAPHS, math, calculateRamping } from "./common.js";

export const DRAW = Object.freeze({
  OFFSET: 20,
  AXIS_LABEL_FONT: "12px roboto",
  TITLE_FONT: "18px roboto",
  ARROW_SIZE: 7,
  ENDPOINT: 150,
});

// ---------------------------------------------------------------------------
// Ramp cache
// ---------------------------------------------------------------------------
const RampCache = new Map();
function cacheKey(width, shape, dir = "+") {
  return `${width}|${shape}|${dir}`;
}
export function getRamp(width, shape, dir = "+") {
  const key = cacheKey(width, shape, dir);
  if (RampCache.has(key)) return RampCache.get(key);
  const ramp = calculateRamping(width, shape, dir);
  RampCache.set(key, ramp);
  return ramp;
}

// ---------------------------------------------------------------------------
// Top-level orchestrator
// ---------------------------------------------------------------------------
export function drawTimingGraphs(ctx, state) {
  const centerX = 5;
  const centerY = state.canvasBlock + DRAW.OFFSET;

  ctx.fillStyle = state.backgroundColor;
  ctx.fillRect(0, 0, state.canvas_width, state.canvas_height);
  ctx.lineWidth = 1;
  ctx.strokeStyle = state.pulseFillStyle;
  ctx.fillStyle = state.axisColor;

  if (state.showPulseTrain) {
    if (state.showPulseTrainRepetition)
      _drawPulseTrainRepetition(ctx, state, centerX, state.canvasBlock * 7);
    _drawPulseTrain(ctx, state, centerX, state.canvasBlock * 4.2);
  }
  _drawPulse(ctx, state, centerX, centerY);
}

// ---------------------------------------------------------------------------
// Draw helpers
// ---------------------------------------------------------------------------
function _drawPulse(ctx, state, centerX, centerY) {
  const title = GRAPHS.Single.charAt(0).toUpperCase() + GRAPHS.Single.slice(1);
  _drawTitle(ctx, state, title, centerY - state.amplitude / 2 - state.padding);
  _drawLabels(ctx, state, centerX, centerY, GRAPHS.Single);
  _pressureSignalToCanvas(
    ctx,
    state,
    _calculatePulseSignal(state, state.canvas_width, GRAPHS.Single),
    centerX,
    centerY,
    GRAPHS.Single,
  );
}

function _drawPulseTrain(ctx, state, centerX, centerY) {
  _drawPulseToTrainLink(ctx, state, centerX, state.canvasBlock * 3);
  _drawLinkRectangle(ctx, state, GRAPHS.Train, centerX, centerY);
  _drawTitle(ctx, state, "Pulse train", centerY - state.amplitude / 2);
  _drawLabels(ctx, state, centerX, centerY, GRAPHS.Train);
  _pressureSignalToCanvas(
    ctx,
    state,
    _calculatePulseTrainSignal(state),
    centerX,
    centerY,
    GRAPHS.Train,
  );
}

function _drawPulseTrainRepetition(ctx, state, centerX, centerY) {
  _drawPulseTrainRepetitionToPulseTrainLink(ctx, state, centerX, state.canvasBlock * 6);
  _drawTitle(ctx, state, "Pulse train repetition", state.canvasBlock * 7 - state.amplitude / 2);
  _drawPulseTrainRepetitionLabels(ctx, state, centerX, centerY);
  _pressureSignalToCanvas(
    ctx,
    state,
    _calculatePulseTrainRepetitionSignal(state),
    centerX,
    centerY,
    GRAPHS.Repetition,
  );
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------
function _drawLabels(ctx, state, centerX, centerY, graph) {
  const canvasWidth = state.canvas_width;
  const endpoint = DRAW.ENDPOINT;
  const step = endpoint / 5;
  const lineHeight = endpoint / 16;
  const maxPulsesToDraw = graph === GRAPHS.Single ? 1 : state.nPulses;
  const pulseDurX = canvasWidth;
  const rampDurX = centerX + (canvasWidth / state.pulseDur) * state.pulse_ramp_duration_ms;

  const pulseRepetitionIntervalX = centerX + canvasWidth / maxPulsesToDraw;
  const rampDurTrainX =
    centerX + ((canvasWidth / maxPulsesToDraw) / state.pulseRepetitionInterval) * state.pulseTrainRampDuration;

  const rampDurLabelHeight = endpoint - 2 * step;
  const pulseDurLabelHeight = endpoint - step;

  ctx.strokeStyle = state.axisColor;
  ctx.font = DRAW.AXIS_LABEL_FONT;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.setLineDash([2, 5]);
  const arrowSize = DRAW.ARROW_SIZE;

  if (graph === GRAPHS.Single) {
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(centerX, centerY + pulseDurLabelHeight);
    ctx.moveTo(rampDurX, centerY);
    ctx.lineTo(rampDurX, centerY + rampDurLabelHeight);
    ctx.moveTo(pulseDurX, centerY);
    ctx.lineTo(pulseDurX, centerY + pulseDurLabelHeight);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.textAlign = "center";

    if (state.pulse_ramp_duration_ms > 0) {
      ctx.fillText(
        `Ramp Duration: ${state.pulse_ramp_duration_ms}ms`,
        rampDurX > 0 ? rampDurX : centerX,
        centerY + rampDurLabelHeight - lineHeight,
      );
      _drawTwoWayArrow(ctx, { x: rampDurX, y: centerY + rampDurLabelHeight }, { x: centerX, y: centerY + rampDurLabelHeight }, arrowSize);
    }

    ctx.textAlign = pulseDurX > 300 ? "center" : "left";
    ctx.fillText(
      `Pulse Duration (PD): ${state.pulseDur}ms`,
      pulseDurX / 2,
      centerY + pulseDurLabelHeight - lineHeight,
    );
    _drawTwoWayArrow(ctx, { x: pulseDurX, y: centerY + pulseDurLabelHeight }, { x: centerX, y: centerY + pulseDurLabelHeight }, arrowSize);
  } else {
    const startLastRamp =
      state.pulseTrainDuration - (state.pulseRepetitionInterval - state.pulseDur) - state.pulseTrainRampDuration;
    const endLastRamp = state.pulseTrainDuration - (state.pulseRepetitionInterval - state.pulseDur);

    const lastRampStartX =
      centerX + ((canvasWidth / maxPulsesToDraw) / state.pulseRepetitionInterval) * startLastRamp;
    const lastRampEndX =
      centerX + ((canvasWidth / maxPulsesToDraw) / state.pulseRepetitionInterval) * endLastRamp;

    const priLabelHeight = endpoint - step;
    const pulseTrainDurationHeight = endpoint;

    ctx.moveTo(centerX, centerY);
    ctx.lineTo(centerX, centerY + pulseTrainDurationHeight);

    if (state.pulseTrainRampDuration > 0) {
      ctx.moveTo(rampDurTrainX, centerY);
      ctx.lineTo(rampDurTrainX, centerY + rampDurLabelHeight);
      ctx.moveTo(lastRampStartX, centerY);
      ctx.lineTo(lastRampStartX, centerY + rampDurLabelHeight);
      ctx.moveTo(lastRampEndX, centerY);
      ctx.lineTo(lastRampEndX, centerY + rampDurLabelHeight);
    }

    ctx.moveTo(pulseRepetitionIntervalX, centerY);
    ctx.lineTo(pulseRepetitionIntervalX, centerY + priLabelHeight);
    ctx.moveTo(canvasWidth, centerY);
    ctx.lineTo(canvasWidth, centerY + pulseTrainDurationHeight);
    ctx.stroke();

    ctx.setLineDash([]);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.textAlign = "left";

    if (state.pulseTrainRampDuration > 0) {
      const label = `Pulse Train Ramp Duration: ${state.pulseTrainRampDuration}ms`;
      ctx.fillText(label, centerX, centerY + rampDurLabelHeight - lineHeight);
      _drawTwoWayArrow(ctx, { x: rampDurTrainX, y: centerY + rampDurLabelHeight }, { x: centerX, y: centerY + rampDurLabelHeight }, arrowSize);
      ctx.fillText(label, lastRampStartX, centerY + rampDurLabelHeight - lineHeight);
      _drawTwoWayArrow(ctx, { x: lastRampStartX, y: centerY + rampDurLabelHeight }, { x: lastRampEndX, y: centerY + rampDurLabelHeight }, arrowSize);
    }

    ctx.textAlign = pulseRepetitionIntervalX > 300 ? "center" : "left";
    const priLabel = `Pulse Repetition Interval (PRI = 1 / PRF): ${state.pulseRepetitionInterval}ms`;
    ctx.fillText(priLabel, pulseRepetitionIntervalX / 2, centerY + priLabelHeight - lineHeight);
    _drawTwoWayArrow(ctx, { x: pulseRepetitionIntervalX - state.padding, y: centerY + priLabelHeight }, { x: centerX, y: centerY + priLabelHeight }, arrowSize);

    ctx.textAlign = "center";
    const ptdLabel = `Pulse Train Duration (PTD): ${state.pulseTrainDuration}ms`;
    ctx.fillText(ptdLabel, canvasWidth / 2, centerY + pulseTrainDurationHeight - lineHeight);
    _drawTwoWayArrow(ctx, { x: canvasWidth, y: centerY + pulseTrainDurationHeight }, { x: centerX, y: centerY + pulseTrainDurationHeight }, arrowSize);
  }

  ctx.font = DRAW.TITLE_FONT.replace("20", "20");
  ctx.setLineDash([]);
  ctx.textAlign = "center";
  ctx.fillText("t", pulseDurX + 30, centerY - 20);
  _drawArrow(ctx, { x: pulseDurX + 20, y: centerY - 10 }, { x: pulseDurX + 50, y: centerY - 10 }, DRAW.ARROW_SIZE);

  ctx.font = DRAW.AXIS_LABEL_FONT;
  ctx.lineWidth = 2;
}

function _drawPulseTrainRepetitionLabels(ctx, state, centerX, centerY) {
  const arrowSize = DRAW.ARROW_SIZE;
  const canvasWidth = state.canvas_width;

  const pulseRepetitionIntervalX =
    centerX + (state.pulseTrainRepetitionIntervalWidth / state.pulseTrainRepetitionInterval) * state.pulseRepetitionInterval;
  const pulseTrainRepetitionIntervalX = centerX + state.pulseTrainRepetitionIntervalWidth;
  const pulseTrainRampDurationX =
    centerX + (state.pulseTrainRepetitionIntervalWidth / state.pulseTrainRepetitionInterval) * state.pulseTrainRepetitionRampDuration;

  const trainOffSet = state.pulseTrainRepetitionInterval - state.pulseTrainDuration;
  const pulseOffset = state.pulseRepetitionInterval - state.pulseDur;
  const offSet = trainOffSet + pulseOffset;

  const startLastRamp = state.pulseTrainRepetitionDuration - offSet - state.pulseTrainRepetitionRampDuration;
  const endLastRamp = state.pulseTrainRepetitionDuration - offSet;

  const lastRampStartX = centerX + (canvasWidth / state.pulseTrainRepetitionDuration) * startLastRamp;
  const lastRampEndX = centerX + (canvasWidth / state.pulseTrainRepetitionDuration) * endLastRamp;

  const endpoint = DRAW.ENDPOINT;
  const step = endpoint / 5;
  const rampLabelHeight = endpoint - 3 * step;
  const pulseRepetitionIntervalHeight = endpoint - 2 * step;
  const pulseDurLabelHeight = endpoint - step;
  const pulseTrainRepetitionIntervalHeight = endpoint;

  ctx.strokeStyle = state.axisColor;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.setLineDash([2, 5]);

  ctx.moveTo(centerX, centerY);
  ctx.lineTo(centerX, centerY + pulseTrainRepetitionIntervalHeight);

  if (state.pulseTrainRepetitionRampDuration > 0) {
    ctx.moveTo(pulseTrainRampDurationX, centerY);
    ctx.lineTo(pulseTrainRampDurationX, centerY + rampLabelHeight);
    ctx.moveTo(lastRampStartX, centerY);
    ctx.lineTo(lastRampStartX, centerY + rampLabelHeight);
    ctx.moveTo(lastRampEndX, centerY);
    ctx.lineTo(lastRampEndX, centerY + rampLabelHeight);
  }

  ctx.moveTo(pulseRepetitionIntervalX, centerY);
  ctx.lineTo(pulseRepetitionIntervalX, centerY + pulseRepetitionIntervalHeight);
  ctx.moveTo(pulseTrainRepetitionIntervalX, centerY);
  ctx.lineTo(pulseTrainRepetitionIntervalX, centerY + pulseDurLabelHeight);
  ctx.moveTo(state.canvas_width, centerY);
  ctx.lineTo(state.canvas_width, centerY + pulseTrainRepetitionIntervalHeight);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.font = DRAW.AXIS_LABEL_FONT;

  if (state.pulseTrainRepetitionRampDuration > 0) {
    const label = `PTR Ramp Duration: ${state.pulseTrainRepetitionRampDuration}ms`;
    ctx.textAlign = "left";
    ctx.fillText(label, centerX + state.padding, centerY + rampLabelHeight - state.padding);
    _drawTwoWayArrow(ctx, { x: centerX, y: centerY + rampLabelHeight }, { x: pulseTrainRampDurationX, y: centerY + rampLabelHeight }, arrowSize);
    _drawTwoWayArrow(ctx, { x: lastRampStartX, y: centerY + rampLabelHeight }, { x: lastRampEndX, y: centerY + rampLabelHeight }, arrowSize);
  }

  ctx.textAlign = pulseRepetitionIntervalX > 300 ? "center" : "left";
  ctx.fillText(
    `Pulse Repetition Interval: ${state.pulseRepetitionInterval}ms`,
    pulseRepetitionIntervalX / 2,
    centerY + pulseRepetitionIntervalHeight - state.padding,
  );
  _drawTwoWayArrow(ctx, { x: centerX, y: centerY + pulseRepetitionIntervalHeight }, { x: pulseRepetitionIntervalX - state.padding, y: centerY + pulseRepetitionIntervalHeight }, arrowSize);

  ctx.textAlign = pulseTrainRepetitionIntervalX > 300 ? "center" : "left";
  ctx.fillText(
    `Pulse Train Repetition Interval: ${state.pulseTrainRepetitionInterval}ms`,
    pulseTrainRepetitionIntervalX / 2,
    centerY + pulseDurLabelHeight - state.padding,
  );
  _drawTwoWayArrow(ctx, { x: centerX, y: centerY + pulseDurLabelHeight }, { x: pulseTrainRepetitionIntervalX - state.padding, y: centerY + pulseDurLabelHeight }, arrowSize);

  ctx.textAlign = state.canvas_width > 300 ? "center" : "left";
  ctx.fillText(
    `Pulse Train Repetition Duration: ${state.pulseTrainRepetitionDuration}ms`,
    state.canvas_width / 2,
    centerY + pulseTrainRepetitionIntervalHeight - state.padding,
  );
  _drawTwoWayArrow(ctx, { x: centerX, y: centerY + pulseTrainRepetitionIntervalHeight }, { x: state.canvas_width, y: centerY + pulseTrainRepetitionIntervalHeight }, arrowSize);

  ctx.font = DRAW.AXIS_LABEL_FONT;
  ctx.lineWidth = 3;
}

// ---------------------------------------------------------------------------
// Signal calculations
// ---------------------------------------------------------------------------
function _calculatePulseSignal(state, pulseDurWidth, shape) {
  if (GRAPHS.Train === shape) {
    const offLen = Math.max(0, Math.floor(state.pulseRepetitionIntervalWidth - pulseDurWidth));
    const pulsePressureSignalOff = math.zeros(offLen);
    return state.getSignal(pulseDurWidth, pulsePressureSignalOff);
  }
  return state.getPulseSignal(pulseDurWidth);
}

function _calculateValuesPulseTrainRampShapeForRepetition(state) {
  let rampUp = [], maxAmpl = [], rampDown = [];

  const timePerPixel = state.pulseTrainRepetitionInterval / state.pulseTrainRepetitionIntervalWidth;
  const tailDurationWidth = (state.pulseRepetitionInterval - state.pulseDur) / timePerPixel;
  const shapeWidth = state.pulseDur / timePerPixel;

  const tailZeros = math.zeros(Math.max(0, Math.floor(tailDurationWidth)));

  if (state.pulse_ramp_duration_ms === 0) {
    maxAmpl = math.ones(Math.max(0, Math.ceil(shapeWidth)));
  } else {
    const alphaOfPulse = Math.floor((1 / state.pulseDur) * (state.pulse_ramp_duration_ms * 2));
    const rampWidth = Math.max(0, Math.floor(shapeWidth * (alphaOfPulse / 2)));
    rampUp = getRamp(rampWidth, state.pulse_ramp_shape, "+");
    maxAmpl = math.ones(Math.max(0, Math.floor(shapeWidth - 2 * rampWidth)));
    rampDown = getRamp(rampWidth, state.pulse_ramp_shape, "-");
  }

  const pulseSignal = math.concat(rampUp, maxAmpl, rampDown, tailZeros);
  return { pulseSignal, tail: tailZeros };
}

function _calculatePulseTrainSignal(state) {
  let pulseTrainSignals = [];
  const pulseShapeWidth = state.pulseDur / (state.pulseRepetitionInterval / state.pulseRepetitionIntervalWidth);

  const singlePulse = _calculatePulseSignal(state, pulseShapeWidth, GRAPHS.Train);
  const tailLength = Math.max(0, Math.floor(state.pulseRepetitionIntervalWidth - pulseShapeWidth));
  const tail = math.zeros(tailLength);

  for (let p = 0; p < Math.ceil(state.nPulses); p += 1) {
    pulseTrainSignals = pulseTrainSignals.concat(singlePulse);
  }

  if (state.pulseTrainRampDuration === 0) return pulseTrainSignals;

  const alphaOfPulseTrain =
    (1 / (state.pulseTrainDuration - (state.pulseRepetitionInterval - state.pulseDur))) * state.pulseTrainRampDuration;
  const pulseTrainShapeWidth = pulseTrainSignals.length - tailLength;
  const rampWidth = Math.max(0, Math.floor(pulseTrainShapeWidth * alphaOfPulseTrain));

  const tUp = getRamp(rampWidth, state.pulseTrainRampShape, "+");
  const tDown = getRamp(rampWidth, state.pulseTrainRampShape, "-");

  const startMax = tUp.length;
  const endMax = pulseTrainSignals.length - tUp.length - tailLength;

  const signalMax = pulseTrainSignals.slice(startMax, endMax);
  const signalUp = math.map(pulseTrainSignals.slice(0, startMax), (v, i) => v * tUp[i]);
  const signalDown = math.map(pulseTrainSignals.slice(endMax), (v, i) => v * tDown[i]);

  return math.concat(signalUp, signalMax, signalDown, tail);
}

function _calculateTrainForTrainRepetition(state, pulseTrainPressureSignals, results, alphaOfPulseTrain, repetitionTailLength) {
  const shapeWidth =
    pulseTrainPressureSignals.length - results.tail.length - repetitionTailLength;
  const rampWidth = Math.max(0, Math.floor(shapeWidth * alphaOfPulseTrain));
  if (rampWidth === 0) return [];

  const tUp = getRamp(rampWidth, state.pulseTrainRampShape, "+");
  const tDown = getRamp(rampWidth, state.pulseTrainRampShape, "-");

  const boundaryUpToMax = tUp.length;
  const signalUp = pulseTrainPressureSignals
    .slice(0, boundaryUpToMax)
    .map((value, index) => value * tUp[index]);

  const boundaryMaxToDown =
    pulseTrainPressureSignals.length - tUp.length - results.tail.length - repetitionTailLength;
  const signalMax = pulseTrainPressureSignals.slice(boundaryUpToMax, boundaryMaxToDown);

  const signalDown = pulseTrainPressureSignals
    .slice(boundaryMaxToDown, -results.tail.length - repetitionTailLength)
    .map((value, index) => value * tDown[index]);

  return signalUp.concat(signalMax, signalDown, results.tail, Array(repetitionTailLength).fill(0));
}

function _calculatePulseTrainRepetitionSignal(state) {
  let pulseTrainRepTotalT = [];
  let pulseTrainPulseSignals = [];

  const results = _calculateValuesPulseTrainRampShapeForRepetition(state);
  const alphaOfPulseTrain =
    (1 / (state.pulseTrainDuration - (state.pulseRepetitionInterval - state.pulseDur))) *
    state.pulseTrainRampDuration;

  for (let p = 0; p < state.nPulses; p += 1) {
    pulseTrainPulseSignals = pulseTrainPulseSignals.concat(results.pulseSignal);
  }

  const timePerPixel = state.pulseTrainRepetitionDuration / state.canvas_width;
  const repetitionTailWidth = (state.pulseTrainRepetitionInterval - state.pulseTrainDuration) / timePerPixel;
  const repetitionTail = repetitionTailWidth > 0 ? math.zeros(Math.floor(repetitionTailWidth)) : [];

  pulseTrainPulseSignals = pulseTrainPulseSignals.concat(repetitionTail);

  let trainSignalForRepetition = pulseTrainPulseSignals;
  if (state.pulseTrainRampDuration > 0) {
    trainSignalForRepetition = _calculateTrainForTrainRepetition(
      state,
      pulseTrainPulseSignals,
      results,
      alphaOfPulseTrain,
      repetitionTail.length,
    );
  }

  const expectedWidth = Math.floor(state.pulseTrainRepetitionIntervalWidth);
  if (trainSignalForRepetition.length < expectedWidth) {
    const pad = math.zeros(expectedWidth - trainSignalForRepetition.length);
    trainSignalForRepetition = math.concat(trainSignalForRepetition, pad);
  } else if (trainSignalForRepetition.length > expectedWidth) {
    trainSignalForRepetition = trainSignalForRepetition.slice(0, expectedWidth);
  }

  for (let t = 0; t < state.nTrains; t += 1) {
    pulseTrainRepTotalT = pulseTrainRepTotalT.concat(trainSignalForRepetition);
  }

  const alphaRep = (1 / state.pulseTrainRepetitionDuration) * state.pulseTrainRepetitionRampDuration;
  const repRampWidth = Math.max(0, Math.floor(state.canvas_width * alphaRep));
  if (state.pulseTrainRepetitionRampDuration === 0 || repRampWidth === 0) return pulseTrainRepTotalT;

  const tUpRep = getRamp(repRampWidth, state.pulseTrainRepetitionRampShape, "+");
  const tDownRep = getRamp(repRampWidth, state.pulseTrainRepetitionRampShape, "-");

  const rampUp = pulseTrainRepTotalT.slice(0, tUpRep.length).map((v, i) => v * tUpRep[i]);
  const startMax = tUpRep.length;
  const endMax =
    pulseTrainRepTotalT.length - tUpRep.length - results.tail.length - repetitionTail.length;
  const mid = pulseTrainRepTotalT.slice(startMax, endMax);
  const rampDown = pulseTrainRepTotalT
    .slice(endMax, endMax + tDownRep.length)
    .map((v, i) => v * tDownRep[i]);

  const used = rampUp.length + (endMax - startMax) + rampDown.length;
  const off = math.zeros(Math.max(0, Math.floor(pulseTrainRepTotalT.length - used)));

  return math.concat(rampUp, mid, rampDown, off);
}

// ---------------------------------------------------------------------------
// Canvas rendering
// ---------------------------------------------------------------------------
function _getSampleRate(state, graph) {
  if (graph === GRAPHS.Train) return state.pulseTrainSampleRate;
  if (graph === GRAPHS.Repetition) return state.pulseTrainRepetitionSampleRate;
  return state.pulseSampleRate;
}

function _pressureSignalToCanvas(ctx, state, arr, startX, startY, graph) {
  let resolution = 1;
  const multiplier = 25;
  const arrLength = arr.length;

  let sRate = _getSampleRate(state, graph);
  if (graph === GRAPHS.Train) {
    resolution = state.pulseTrainDuration / state.pulseDur;
    sRate = _getSampleRate(state, graph) / resolution;
  } else if (graph === GRAPHS.Repetition) {
    const trainRes = state.pulseTrainDuration / state.pulseDur;
    const repRes = state.pulseTrainRepetitionDuration / state.pulseRepetitionInterval;
    resolution = Math.max(trainRes, repRes);
    sRate = _getSampleRate(state, graph) / resolution;
  }

  ctx.setLineDash([]);
  ctx.fillStyle = state.pulseFillStyle;
  ctx.strokeStyle = state.pulseFillStyle;
  ctx.lineWidth = 0.8;

  ctx.save();
  ctx.translate(startX, startY);
  ctx.beginPath();

  let pulseArrayIndex = 0;
  const drawBars = [GRAPHS.Train, GRAPHS.Repetition].includes(graph) && resolution >= 6;

  for (let x = 0; x < arrLength; x++) {
    if (arr[x - 1] === 0 && arr[x] > 0) pulseArrayIndex = 0;

    const v = Number.isFinite(arr[x]) ? arr[x] : 0;
    const yNorm = (v * state.amplitude) / 3;

    if (drawBars) {
      ctx.lineTo(x, yNorm);
      ctx.lineTo(x, -yNorm);
    } else {
      const y = -(yNorm * Math.sin((Math.PI * pulseArrayIndex * multiplier) / sRate));
      ctx.lineTo(x, y);
    }
    pulseArrayIndex++;
  }

  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = state.textColor;
}

function _drawPulseToTrainLink(ctx, state, centerX, centerY) {
  const top = state.canvasBlock + 20 + state.amplitude / 2;
  if (state.nPulses <= 1) return;

  ctx.lineWidth = 4;
  ctx.beginPath();

  const gradient = ctx.createLinearGradient(
    centerX,
    centerY + state.canvasBlock - state.amplitude - state.padding,
    centerX,
    top,
  );

  gradient.addColorStop(0, state.linkColor);
  gradient.addColorStop(0.75, state.linkColor);
  gradient.addColorStop(1, state.backgroundColor);

  ctx.fillStyle = gradient;

  const pulseWidth = (state.pulseRepetitionIntervalWidth / state.pulseRepetitionInterval) * state.pulseDur;

  ctx.moveTo(centerX + pulseWidth, centerY + state.canvasBlock - state.amplitude / 2);
  ctx.lineTo(centerX + state.pulseRepetitionIntervalWidth * state.nPulses - state.padding, top);
  ctx.lineTo(centerX, top);
  ctx.lineTo(centerX, centerY + state.canvasBlock - state.amplitude / 2);

  ctx.closePath();
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = state.pulseFillStyle;
  ctx.fillStyle = state.axisColor;
}

function _drawPulseTrainRepetitionToPulseTrainLink(ctx, state, centerX, centerY) {
  const pulseTrainDurationWidth =
    state.pulseTrainDuration / (state.pulseTrainRepetitionInterval / state.pulseTrainRepetitionIntervalWidth);
  const pWidth = pulseTrainDurationWidth / state.nPulses;
  ctx.fillStyle = state.linkColor;
  ctx.lineWidth = 4;

  if (state.nTrains >= 1) {
    ctx.beginPath();
    ctx.setLineDash([2, 5]);
    const gradient = ctx.createLinearGradient(
      centerX,
      centerY + state.amplitude / 2,
      centerX,
      centerY - state.amplitude,
    );
    gradient.addColorStop(0, state.linkColor);
    gradient.addColorStop(0.75, state.linkColor);
    gradient.addColorStop(1, state.backgroundColor);
    ctx.fillStyle = gradient;

    ctx.moveTo(centerX + pWidth * state.nPulses - state.padding, centerY + state.canvasBlock - 50);
    ctx.lineTo(state.canvas_width, centerY - state.amplitude);
    ctx.lineTo(centerX, centerY - state.amplitude);
    ctx.lineTo(centerX, centerY + state.canvasBlock - 50);

    ctx.closePath();
    ctx.fill();
    ctx.setLineDash([]);

    const gradientRect = ctx.createLinearGradient(centerX, centerY + state.amplitude + 6 * state.padding, centerX, centerY);
    gradientRect.addColorStop(1, state.linkColor);
    gradientRect.addColorStop(0.75, state.linkColor);
    gradientRect.addColorStop(0, state.backgroundColor);
    ctx.fillStyle = gradientRect;

    ctx.fillRect(centerX, centerY, pWidth * state.nPulses, state.amplitude + 6 * state.padding);
  }

  ctx.lineWidth = 3;
  ctx.strokeStyle = state.pulseFillStyle;
  ctx.fillStyle = state.axisColor;
}

function _drawTwoWayArrow(ctx, p1, p2, size) {
  _drawArrow(ctx, p1, p2, size);
  _drawArrow(ctx, p2, p1, size);
}

function _drawArrow(ctx, p1, p2, size) {
  const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const hyp = Math.hypot(dx, dy);

  ctx.save();
  ctx.translate(p1.x, p1.y);
  ctx.rotate(angle);

  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(hyp - size, 0);
  ctx.stroke();
  ctx.closePath();

  ctx.beginPath();
  ctx.lineTo(hyp - size, size);
  ctx.lineTo(hyp, 0);
  ctx.lineTo(hyp - size, -size);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

function _drawTitle(ctx, state, title, y) {
  ctx.font = DRAW.TITLE_FONT;
  ctx.textAlign = "center";
  ctx.fillText(title, state.canvas_width / 2, y);
  ctx.textAlign = "left";
}

function _drawLinkRectangle(ctx, state, graph, centerX, centerY) {
  if (graph === GRAPHS.Single || state.nPulses <= 1) return;

  const gradientRect = ctx.createLinearGradient(
    centerX,
    centerY + state.amplitude,
    centerX,
    centerY - state.amplitude,
  );
  gradientRect.addColorStop(0, state.backgroundColor);
  gradientRect.addColorStop(0.95, state.linkColor);
  gradientRect.addColorStop(1, state.linkColor);

  ctx.fillStyle = gradientRect;

  const pulseWidth = (state.pulseRepetitionIntervalWidth / state.pulseRepetitionInterval) * state.pulseDur;
  ctx.fillRect(centerX, centerY - state.amplitude, pulseWidth, 1.5 * state.amplitude);

  ctx.closePath();
  ctx.lineWidth = 3;
  ctx.strokeStyle = state.pulseFillStyle;
  ctx.fillStyle = state.axisColor;
}
